<script setup lang="ts">
import { computed, ref } from 'vue'

import { defaultSignCalculator, signCalculators } from '../SignCalculator/calculators'

import type { SignCalculatorId, SignResult } from '../SignCalculator/calculators'

type ParagraphParam = { key?: string, value?: string }

const props = withDefaults(defineProps<{
  params?: ParagraphParam[]
}>(), {
  params: () => []
})

const calculatorId = computed<SignCalculatorId>(() => {
  const configured = props.params.find(({ key }) => key === 'calculator')?.value
  return configured && configured in signCalculators ? configured as SignCalculatorId : defaultSignCalculator
})

const calculator = computed(() => signCalculators[calculatorId.value])

const headline = computed(() => calculator.value.headline)
const minDate = computed(() => calculator.value.minDate)
const maxDate = computed(() => calculator.value.maxDate)

const dateOfBirth = ref('')
const result = ref<SignResult | null>(null)
const errorMessage = ref('')

const handleSubmit = () => {
  errorMessage.value = ''
  result.value = null

  const [year, month, day] = dateOfBirth.value.split('-').map(Number)
  const sign = calculator.value.calculate(day, month, year)

  if (!sign) {
    errorMessage.value = `Für Geburtsdaten außerhalb von ${calculator.value.rangeLabel} liegen leider keine Daten vor.`
    return
  }

  result.value = sign
}

const resetFormAndResult = () => {
  dateOfBirth.value = ''
  result.value = null
  errorMessage.value = ''
}
</script>

<template>
  <div class="bx-content bx-sign-calculator"
       data-tc="sign-calculator">
    <h2 class="bx-typo--h3"
        data-tc="sign-calculator-headline">
      {{ headline }}
    </h2>
    <form class="bx-form bx-typo--form bx-typo--form-contact"
          @submit.prevent="handleSubmit">
      <div v-if="!result">
        <div class="bx-form__item">
          <label for="dateOfBirth"
                 class="bx-form__label">
            Geburtsdatum *
          </label>
          <input id="dateOfBirth"
                 v-model="dateOfBirth"
                 type="date"
                 :min="minDate"
                 :max="maxDate"
                 data-tc="sign-calculator-date">
        </div>
        <div class="bx-form__item bx-form__item--centered">
          <button type="submit"
                  :disabled="!dateOfBirth"
                  class="bx-form__button"
                  data-tc="sign-calculator-submit">
            Rechner starten
          </button>
        </div>
        <div class="bx-form__disclaimer-required bx-sign-calculator__required">
          * Pflichtfeld
        </div>
        <div v-if="errorMessage"
             class="bx-form__message bx-form__message--error">
          {{ errorMessage }}
        </div>
      </div>
      <div v-if="result"
           class="bx-sign-calculator__result bx-content__continuous-text bx-typo--content-continuous-text"
           data-tc="sign-calculator-result">
        <p>
          Ihr <strong>{{ calculator.resultLabel }}</strong> ist
          <strong data-tc="sign-calculator-value">{{ result.sign }}</strong><template v-if="result.element">
            mit dem Element <strong data-tc="sign-calculator-element">{{ result.element }}</strong>
          </template>.
        </p>
        <p v-if="result.link">
          <a :href="result.link.href"
             data-tc="sign-calculator-link">{{ result.link.label }}</a>
        </p>
        <div class="bx-form__item bx-form__item--centered">
          <button class="bx-form__button"
                  @click.prevent="resetFormAndResult()">
            Neu berechnen
          </button>
        </div>
      </div>
    </form>
  </div>
</template>
